/**
 * @fileoverview The strip of photographs at the top of a PileCard.
 *
 * Each picture is a button that opens the sheet for that one photograph, when
 * there is anywhere for it to go, with a × of its own to remove it (once
 * removed, it is gone). Split out of PileCard to keep that file under 800
 * lines (CLAUDE.md rule 5, B107); the look and behaviour are unchanged.
 *
 * Where: PileCard, at the top of every group.
 * API: none; the page's callbacks arrive as props.
 * Words (en/es): tray.pile.photoOptions, tray.pile.removePhoto.
 *
 * @module PhotoStrip
 */

import React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { CloseX } from '@/shared/components/ui/CloseX';
import { PhotoMark } from '@/shared/components/ui/PhotoButton';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { TrayPhoto } from '@/shared/api/tray';

/** What the strip needs to draw a group's pictures. */
interface PhotoStripProps {
  /** The group's photos, as drawn (removed ones are not in it). */
  photos: TrayPhoto[];
  /** True while one of the pile's answers is being saved. */
  busy: boolean;
  /** There is somewhere else a photo could go, so each one can be tapped to move it. */
  canMove: boolean;
  /** True while the model (or the packet reader) is looking at the pile: the pictures pulse. */
  looking: boolean;
  /** Opens the sheet for one photo: move it, give it its own group, or remove it. */
  onMovePhoto: (photo: TrayPhoto) => void;
  /** Removes one photo (its ×): it is gone at once. */
  onRemovePhoto: (photo: TrayPhoto) => void;
}

/**
 * The group's pictures, large, side by side; each opens its sheet and has a × to remove it.
 *
 * @param props - PhotoStripProps.
 */
export const PhotoStrip: React.FC<PhotoStripProps> = ({ photos, busy, canMove, looking, onMovePhoto, onRemovePhoto }) => {
  const { reactTranslate: t } = useTranslation();
  /** One picture's size: large when the group has one. */
  const size = photos.length === 1 ? 'h-40 w-40' : 'h-28 w-28';
  return (
    <div className="flex gap-1 overflow-x-auto bg-neutral-900 p-1.5">
      {photos.map((photo, n) => (
        <div key={photo.tray_id} className="relative shrink-0">
          <button
            type="button"
            onClick={() => onMovePhoto(photo)}
            disabled={busy || !canMove}
            aria-label={t('tray.pile.photoOptions', { n: n + 1, total: photos.length })}
            className={cn(
              'relative block rounded-sm',
              canMove && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white active:opacity-80',
            )}
          >
            <span className={cn('relative block overflow-hidden rounded-sm bg-muted data-[small=true]:bg-transparent', size)}>
              <FramedPhoto
                src={photo.thumbUrl}
                alt=""
                className={cn(
                  'transition-opacity',
                  looking && 'opacity-60 motion-safe:animate-pulse',
                )}
                loading="lazy"
              />
            </span>
            {photos.length > 1 && (
              <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-xs text-white">{n + 1}</span>
            )}
            {/* A hint that the picture can be tapped, without a word. */}
            {canMove && (
              <PhotoMark tone="light" size="xs" className="absolute bottom-1 right-1">
                <MoreHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
              </PhotoMark>
            )}
          </button>
          {/* Remove it: a thumb's tap, in the corner, where the eye already is. */}
          {/* A solid card-coloured circle with an edge and a shadow, so it reads as a button on any photo; 32px to see, 44px to hit (B107). */}
          <CloseX
            label={t('tray.pile.removePhoto')}
            onClick={() => onRemovePhoto(photo)} disabled={busy}
            className="absolute right-1.5 top-1.5"
          />
        </div>
      ))}
    </div>
  );
};
